import React, { useEffect, useState } from "react";
import { Bone, CheckCircle2, XCircle, Loader2, LogOut, Download, ChevronDown, ChevronUp, Store, Star } from "lucide-react";
import { venueApi, venueAuth } from "../lib/venueApi";

/**
 * Página /canjear — portal del bar para validar cupones.
 * - Sin PIN: muestra pantalla de login
 * - Con PIN (token guardado): muestra el canjeador + stats + listado de canjes
 */
export default function Redeem() {
  const [venue, setVenue] = useState(venueAuth.getVenue());
  const [token, setToken] = useState(venueAuth.getToken());

  if (!token || !venue) {
    return <PinLogin onLogin={(t, v) => { setToken(t); setVenue(v); }} />;
  }
  return <VenueDashboard venue={venue} onLogout={() => { venueAuth.clear(); setVenue(null); setToken(null); }} />;
}

function PinLogin({ onLogin }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await venueApi.loginWithPin(pin.trim().toUpperCase());
      venueAuth.setSession(res.token, res.venue);
      onLogin(res.token, res.venue);
    } catch (err) {
      setError(err.message || "PIN inválido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="canjear-login-page">
      <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FEBA01] p-8 shadow-brutal-white">
        <div className="flex items-center gap-3 mb-6">
          <Store className="text-[#FEBA01]" size={36} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FEBA01]">Kiltro Scout</div>
            <h1 className="font-display text-3xl uppercase tracking-tight">
              <span className="text-white">Reclamar</span> <span className="text-[#FEBA01]">Hueso</span>
            </h1>
          </div>
        </div>

        <p className="text-sm text-[#A3A3A3] mb-6">
          Ingresa el PIN único de tu local. Lo recibiste por WhatsApp del equipo Kiltro y te permite validar los Huesos que llegan al local.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-[#FEBA01] mb-2">PIN del bar</label>
            <input
              type="text"
              value={pin}
              onChange={(e) => setPin(e.target.value.toUpperCase())}
              placeholder="EJ: KROSS-7421"
              autoComplete="off"
              className="w-full bg-black border-2 border-[#333] focus:border-[#FEBA01] outline-none px-4 py-3 font-mono text-lg uppercase tracking-widest text-[#FEBA01] dialog-legible"
              data-testid="pin-input"
              required
            />
          </div>
          {error && (
            <div className="text-sm text-red-400 border border-red-900 bg-red-950/40 px-3 py-2 flex items-start gap-2" data-testid="pin-error">
              <XCircle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={loading || !pin.trim()}
            className="w-full btn-brutal bg-[#FEBA01] text-black font-display text-lg uppercase tracking-wide px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            data-testid="pin-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Bone size={18} />}
            {loading ? "Validando..." : "Entrar"}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[#222] text-xs text-[#666] font-mono uppercase tracking-widest text-center">
          ¿Problemas? Escríbenos a contacto@kiltroscout.cl
        </div>
      </div>
    </div>
  );
}

function VenueDashboard({ venue, onLogout }) {
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState([]);
  const [showDetail, setShowDetail] = useState(false);

  const loadStats = async () => {
    try { setStats(await venueApi.stats()); } catch { /* silent */ }
  };
  const loadClaims = async () => {
    try { setClaims(await venueApi.claims(30)); } catch { /* silent */ }
  };

  useEffect(() => {
    loadStats();
    loadClaims();
  }, []);

  const redeem = async (e) => {
    e.preventDefault();
    setResult(null);
    setRedeeming(true);
    try {
      const res = await venueApi.redeem(code.trim().toUpperCase());
      setResult({ ok: true, claim: res.claim });
      setCode("");
      loadStats();
      loadClaims();
    } catch (err) {
      setResult({ ok: false, message: err.detail || err.message });
    } finally {
      setRedeeming(false);
    }
  };

  const downloadCsv = () => {
    const token = venueAuth.getToken();
    // Abrir en tab nueva con auth header NO es posible con window.open.
    // Estrategia: fetch blob + descargar.
    fetch(venueApi.claimsCsvUrl(), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `canjes_${venue.name.replace(/\s+/g, "_")}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="min-h-screen bg-black text-[#F5F5F5]" data-testid="canjear-dashboard">
      <header className="sticky top-0 z-30 bg-[#0A0A0A] border-b-2 border-[#FEBA01] px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#FEBA01]">Kiltro Scout · Local</div>
          <div className="font-display text-xl leading-none mt-0.5">{venue.name}</div>
          {venue.neighborhood && <div className="text-[11px] text-[#A3A3A3]">{venue.neighborhood}</div>}
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="text-[#A3A3A3] hover:text-[#FEBA01] p-2"
          title="Cerrar sesión"
          data-testid="venue-logout"
        >
          <LogOut size={18} />
        </button>
      </header>

      <div className="max-w-xl mx-auto px-4 py-6 space-y-6">
        {/* Canjear */}
        <section className="bg-[#0A0A0A] border-2 border-[#FEBA01] p-5 shadow-brutal-white">
          <h2 className="font-display text-2xl uppercase tracking-tight mb-3">Reclamar Hueso</h2>
          <form onSubmit={redeem} className="space-y-3">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="KIL-XXXXXXXX"
              autoComplete="off"
              className="w-full bg-black border-2 border-[#333] focus:border-[#FEBA01] outline-none px-4 py-4 font-mono text-xl uppercase tracking-widest text-[#FEBA01] dialog-legible"
              data-testid="redeem-code-input"
              required
            />
            <button
              type="submit"
              disabled={redeeming || !code.trim()}
              className="w-full btn-brutal bg-[#FEBA01] text-black font-display text-lg uppercase tracking-wide px-6 py-3 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="redeem-submit"
            >
              {redeeming ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
              {redeeming ? "Validando..." : "Validar y canjear"}
            </button>
          </form>

          {result && (
            <div
              className={`mt-4 p-4 border-2 ${result.ok ? "border-green-600 bg-green-950/40" : "border-red-700 bg-red-950/40"}`}
              data-testid="redeem-result"
            >
              {result.ok ? (
                <>
                  <div className="flex items-center gap-2 text-green-400 font-display text-lg uppercase"><CheckCircle2 size={20} /> Reclamado</div>
                  <div className="mt-2 text-sm">
                    <div><strong>{result.claim.title}</strong> · {result.claim.discount}</div>
                    <div className="text-[#A3A3A3]">Cliente: {result.claim.user_handle || "anónimo"}</div>
                    <div className="font-mono text-xs text-[#FEBA01] mt-1">{result.claim.code}</div>
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2 text-red-400">
                  <XCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-display text-lg uppercase">No se pudo reclamar</div>
                    <div className="text-sm mt-1">{result.message}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Stats */}
        {stats && (
          <section className="grid grid-cols-2 gap-3" data-testid="venue-stats">
            <StatCard label="Hoy" value={stats.redeemed_today} hint="reclamos" />
            <StatCard label="Este mes" value={stats.redeemed_month} hint="reclamos" />
            <StatCard label="Pendientes" value={stats.pending} hint="sin reclamar" />
            <div className="bg-[#0A0A0A] border-2 border-[#333] p-3 flex flex-col justify-center">
              <div className="font-mono text-[9px] uppercase tracking-widest text-[#FEBA01]">Rating</div>
              <div className="font-display text-2xl text-[#FEBA01] flex items-center gap-1">
                {stats.rating_avg?.toFixed(1) || "-"} <Star size={16} />
              </div>
              <div className="text-[10px] text-[#A3A3A3]">{stats.rating_count || 0} reseñas</div>
            </div>
            {stats.top_coupon && (
              <div className="col-span-2 bg-[#FEBA01]/10 border-2 border-[#FEBA01] p-3">
                <div className="font-mono text-[9px] uppercase tracking-widest text-[#FEBA01]">Hueso estrella del mes</div>
                <div className="font-display text-lg mt-0.5">{stats.top_coupon.title}</div>
                <div className="text-xs text-[#A3A3A3]">{stats.top_coupon.count} reclamos</div>
              </div>
            )}
          </section>
        )}

        {/* Detail dropdown */}
        <section className="bg-[#0A0A0A] border-2 border-[#333]">
          <button
            type="button"
            onClick={() => setShowDetail(!showDetail)}
            className="w-full flex items-center justify-between px-4 py-3 font-display uppercase tracking-wide text-[#FEBA01]"
            data-testid="toggle-claims-detail"
          >
            <span>Detalle de reclamos (últimos 30 días)</span>
            {showDetail ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          {showDetail && (
            <div className="border-t border-[#333]">
              <div className="px-4 py-2 flex justify-between items-center text-xs text-[#A3A3A3]">
                <span>{claims.length} registros</span>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="inline-flex items-center gap-1 text-[#FEBA01] hover:text-white"
                  data-testid="venue-export-csv"
                >
                  <Download size={14} /> Descargar Excel
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[#111] sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-widest text-[#FEBA01] text-[9px]">Código</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-widest text-[#FEBA01] text-[9px]">Hueso</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-widest text-[#FEBA01] text-[9px]">Fecha</th>
                      <th className="px-3 py-2 text-left font-mono uppercase tracking-widest text-[#FEBA01] text-[9px]">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-[#666]">Aún no hay reclamos</td></tr>
                    )}
                    {claims.map((c) => {
                      const date = c.redeemed_at || c.claimed_at;
                      const label = formatDate(date);
                      return (
                        <tr key={c.id} className="border-t border-[#222]">
                          <td className="px-3 py-2 font-mono text-[#FEBA01]">{c.claim_code}</td>
                          <td className="px-3 py-2 text-[#E5E5E5]">{c.title}</td>
                          <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap">{label}</td>
                          <td className="px-3 py-2">
                            {c.redeemed ? (
                              <span className="text-green-400 font-mono text-[10px]">RECLAMADO</span>
                            ) : (
                              <span className="text-[#A3A3A3] font-mono text-[10px]">PENDIENTE</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-[#0A0A0A] border-2 border-[#333] p-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#FEBA01]">{label}</div>
      <div className="font-display text-3xl text-[#FEBA01] leading-none mt-1">{value ?? 0}</div>
      <div className="text-[10px] text-[#A3A3A3] mt-0.5">{hint}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
